import { useState, useCallback } from "react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import { AdminProduct } from "../types/product.types";

interface UseProductActionsOptions {
  role?: "admin" | "seller";
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export function useProductActions(options: UseProductActionsOptions = {}) {
  const { role = "admin", onSuccess, onError } = options;
  const isAdmin = role === "admin";
  const [loading, setLoading] = useState(false);

  const showToast = useCallback(
    (type: "success" | "error" | "info", message: string) => {
      if (type === "error") {
        onError?.(message);
      }
    },
    [onError]
  );

  const approveProduct = useCallback(async (productId: string) => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const response = await adminService.approveProduct(productId);
      if (response.success) {
        showToast("success", "Product approved successfully");
        onSuccess?.();
        return true;
      } else {
        showToast("error", response.message || "Failed to approve product");
        return false;
      }
    } catch (err) {
      console.error("Error approving product:", err);
      showToast("error", "Failed to approve product. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showToast, onSuccess]);

  const rejectProduct = useCallback(async (productId: string, reason: string) => {
    if (!isAdmin) return false;
    const trimmed = reason.trim();
    if (!trimmed) {
      showToast("error", "Please enter a rejection reason.");
      return false;
    }
    try {
      setLoading(true);
      const response = await adminService.rejectProduct(productId, trimmed);
      if (response.success) {
        showToast("success", "Product rejected successfully");
        onSuccess?.();
        return true;
      } else {
        showToast("error", response.message || "Failed to reject product");
        return false;
      }
    } catch (err) {
      console.error("Error rejecting product:", err);
      showToast("error", "Failed to reject product. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showToast, onSuccess, onError]);

  const submitForApproval = useCallback(async (productId: string) => {
    if (isAdmin) return;
    try {
      setLoading(true);
      const response = await sellerService.submitProductForApproval(productId, {});
      if (response.success) {
        showToast("success", "Product submitted for approval successfully");
        onSuccess?.();
        return true;
      } else {
        showToast("error", response.message || "Failed to submit product");
        return false;
      }
    } catch (err) {
      console.error("Error submitting product:", err);
      showToast("error", "Failed to submit product. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showToast, onSuccess]);

  const deleteProduct = useCallback(async (productId: string) => {
    try {
      setLoading(true);
      const response = isAdmin
        ? await adminService.deleteProduct(productId)
        : await sellerService.deleteProduct(productId);
      if (response.success) {
        showToast("success", "Product deleted successfully");
        onSuccess?.();
        return true;
      } else {
        showToast("error", response.message || "Failed to delete product");
        return false;
      }
    } catch (err) {
      console.error("Error deleting product:", err);
      showToast("error", "Failed to delete product. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showToast, onSuccess]);

  const updateProduct = useCallback(async (productId: string, productData: any) => {
    try {
      setLoading(true);
      const response = isAdmin
        ? await adminService.updateProductAsAdmin(productId, productData)
        : await sellerService.updateProductAsSeller(productId, productData);
      if (response.success) {
        showToast("success", "Product updated successfully!");
        onSuccess?.();
        return true;
      } else {
        showToast("error", response.message || "Failed to update product");
        return false;
      }
    } catch (error) {
      console.error("Error updating product:", error);
      showToast("error", "Failed to update product. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showToast, onSuccess]);

  const saveAsDraft = useCallback(async (productId: string, productData: any) => {
    try {
      setLoading(true);
      const response = isAdmin
        ? await adminService.updateProductToDraft(productId, productData)
        : await sellerService.updateProductToDraft(productId, productData);
      if (response.success) {
        showToast("success", "Product saved as draft!");
        onSuccess?.();
        return true;
      } else {
        showToast("error", response.message || "Failed to save as draft");
        return false;
      }
    } catch (error) {
      console.error("Error saving as draft:", error);
      showToast("error", "Failed to save as draft. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showToast, onSuccess]);

  const publishProduct = useCallback(async (productId: string, productData: any) => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const response = await adminService.updateProductToPublished(productId, productData);
      if (response.success) {
        showToast("success", "Product published successfully!");
        onSuccess?.();
        return true;
      } else {
        showToast("error", response.message || "Failed to publish product");
        return false;
      }
    } catch (error) {
      console.error("Error publishing product:", error);
      showToast("error", "Failed to publish product. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showToast, onSuccess]);

  const updateProductActiveStatus = useCallback(
    async (productId: string, isActive: boolean) => {
      try {
        setLoading(true);
        let response;

        if (isAdmin) {
          response = await adminService.updateProductActiveStatusAsAdmin(
            productId,
            isActive
          );
        } else {
          // Seller flow retained for compatibility.
          const payload = { isEnabled: isActive };
          response = await sellerService.updateProductAsSeller(productId, payload);
        }
        if (response.success) {
          onSuccess?.();
          return true;
        } else {
          showToast("error", response.message || "Failed to update active status");
          return false;
        }
      } catch (error) {
        console.error("Error updating product active status:", error);
        showToast("error", "Failed to update active status. Please try again.");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, showToast, onSuccess]
  );

  return {
    loading,
    approveProduct,
    rejectProduct,
    submitForApproval,
    deleteProduct,
    updateProduct,
    saveAsDraft,
    publishProduct,
    updateProductActiveStatus,
  };
}

